import { useEffect, useMemo, useRef, useState } from "react";
import {
  resolveHostPart,
  resolveHostRows,
  type Part,
  type Values,
  type Project,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { Control, SearchableSelect, type SelectLoader } from "./components";

type Row = { id: string; name: string; sku?: string; values?: Values };

/**
 * Host-provided profile data and part overrides. Search runs inside the
 * dropdown against the host callback, so records that were never loaded can
 * still be found; results are shown in batches while scrolling.
 */
export function HostFormData({
  kind,
  onValues,
  onPart,
  project,
}: {
  kind: "profiles" | "parts";
  project?: Project;
  onValues?: (values: Values) => void;
  onPart?: (part: Part) => void;
}) {
  const services = useAuthoringServices();
  const [selected, setSelected] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const rows = useRef(new Map<string, Row>());
  const selection = useRef<AbortController>(undefined);
  useEffect(() => () => selection.current?.abort(), []);
  const available =
    kind === "profiles"
      ? !!services.listFormProfiles
      : !!(services.listPartOverrides || services.searchPartSkus);
  const load = useMemo<SelectLoader | undefined>(() => {
    if (!available) return undefined;
    // Host callbacks return every match for a query; page through them locally.
    const cache = new Map<string, Promise<Row[]>>();
    const fetchAll = (query: string, signal: AbortSignal) => {
      const key = query.trim();
      if (!cache.has(key)) {
        const request =
          kind === "profiles"
            ? services.listFormProfiles!(key, signal, project)
            : key
              ? (services.searchPartSkus || services.listPartOverrides)!(
                  key,
                  signal,
                )
              : (services.listPartOverrides || services.searchPartSkus)!(
                  key,
                  signal,
                );
        // Same contract as source inputs: rows or { data, translate }.
        const pending = resolveHostRows(
          { version: 1, kind, query: key },
          request,
          signal,
        ).then(
          (rows) => rows as Row[],
          (cause) => {
            cache.delete(key);
            throw cause;
          },
        );
        cache.set(key, pending);
      }
      return cache.get(key)!;
    };
    return async (query, offset, limit, signal) => {
      const all = await fetchAll(query, signal);
      for (const row of all) rows.current.set(row.id, row);
      return {
        items: all.slice(offset, offset + limit).map((row) => ({
          value: row.id,
          label: row.name,
          detail: row.sku,
        })),
        total: all.length,
      };
    };
  }, [
    available,
    kind,
    project,
    services.listFormProfiles,
    services.listPartOverrides,
    services.searchPartSkus,
  ]);
  if (!load) return null;
  const choose = (id: string) => {
    selection.current?.abort();
    setSelected(id);
    setError("");
    const row = rows.current.get(id);
    if (!row) return;
    if (kind === "profiles" && row.values) onValues?.(row.values);
    else if (kind === "parts" && services.loadPartOverride) {
      const controller = new AbortController();
      selection.current = controller;
      setBusy(true);
      void resolveHostPart(
        row.id,
        services.loadPartOverride(row.id, controller.signal),
        controller.signal,
      )
        .then((part) => {
          if (!controller.signal.aborted) onPart?.(part);
        })
        .catch((cause) => {
          if (!controller.signal.aborted) setError(cause.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) setBusy(false);
        });
    }
  };
  const label =
    kind === "profiles"
      ? "Existing global profile data"
      : "Existing part overrides";
  return (
    <section className="panel padded">
      <Control label={label}>
        <SearchableSelect
          label={label}
          value={selected}
          onChange={choose}
          load={load}
          selectedLabel={rows.current.get(selected)?.name}
          searchPlaceholder={
            kind === "profiles" ? "Search profiles…" : "Search part SKUs…"
          }
        />
      </Control>
      {error && <p role="alert">{error}</p>}
      {busy && <p role="status">Loading…</p>}
    </section>
  );
}
